import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from 'react-router-dom';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Heading, tokens } from '@treams/treams-ui';

import LearningEventModalID from '../../../../../../src/packages/kakapo-ui-application/pages/learning/pages/LearningEventModal';
import JourneyList from '../../../../../../src/packages/kakapo-ui-kit/components/journey/JourneyList';
import { JourneyItem } from '../../../../../../src/packages/kakapo-ui-kit/components/journey/JourneyListItem';

const Title = styled(Heading)`
	margin-bottom: ${tokens.gap.group}px;
`;

export default function JourneyListContainer(props: { user: graphql.User }) {
	const { t } = useTranslation();
	const [items, setItems] = useState<JourneyItem[]>();

	const { user } = props;

	const {
		data: goalsData,
		loading: isLoadingGoals,
		error: goalsError,
	} = graphql.useIndividualGoalsQuery({
		variables: { id: user.id, status: graphql.GoalStatus.Active, last: 10 },
	});
	const goals = useMemo(() => goalsData?.user.goalsV2.edges.map(({ node }) => node), [goalsData]);

	const {
		data: archivedGoalsData,
		loading: isLoadingArchivedGoals,
		error: archivedGoalsError,
	} = graphql.useIndividualGoalsQuery({
		variables: { id: user.id, status: graphql.GoalStatus.Archived, last: 10 },
	});
	const archivedGoals = useMemo(
		() => archivedGoalsData?.user.goalsV2.edges.map(({ node }) => node),
		[archivedGoalsData],
	);

	const {
		data: completedGoalsData,
		loading: isLoadingCompletedGoals,
		error: completedGoalsError,
	} = graphql.useIndividualGoalsQuery({
		variables: { id: user.id, status: graphql.GoalStatus.Completed, last: 10 },
	});
	const completedGoals = useMemo(
		() => completedGoalsData?.user.goalsV2.edges.map(({ node }) => node),
		[completedGoalsData],
	);

	const [now] = useState(new Date().toString());

	const attendedLearningEvents = graphql.useAttendedLearningEventsForJourneyQuery({
		variables: {
			now,
			userID: user.id,
		},
	});

	const {
		data: evaluationsV2,
		loading: isLoadingEvaluationsV2,
		error: evaluationsV2Error,
	} = graphql.useEvaluationSubmissionsForJourneyQuery({
		variables: {
			userID: user.id,
		},
	});

	const feedbackV2 = graphql.useFeedbackRequestsQuery({
		variables: {
			userID: user.id,
			isArchived: false,
			last: 10,
		},
	});

	useEffect(() => {
		const itemArray: JourneyItem[] = [];

		setItems(
			itemArray
				.concat(goals ? (goals as graphql.Goal[]) : [])
				.concat(archivedGoals ? (archivedGoals as graphql.Goal[]) : [])
				.concat(completedGoals ? (completedGoals as graphql.Goal[]) : [])
				.concat(evaluationsV2?.user?.evaluationSubmissions?.edges.map((e) => e.node) ?? [])
				.concat(
					feedbackV2?.data?.user.feedbackRequests.edges.map(
						(e) => e.node as graphql.FeedbackRequest,
					) ?? [],
				)
				.concat(
					attendedLearningEvents.data?.user.attendingLearningEvents.edges.map(
						(edge) => edge.node,
					) ?? [],
				),
		);
	}, [
		goals,
		archivedGoals,
		completedGoals,
		attendedLearningEvents.data,
		evaluationsV2,
		feedbackV2.data,
	]);

	const isLoading =
		isLoadingGoals ||
		isLoadingCompletedGoals ||
		isLoadingArchivedGoals ||
		isLoadingEvaluationsV2 ||
		attendedLearningEvents.loading;
	const error =
		goalsError ??
		completedGoalsError ??
		archivedGoalsError ??
		evaluationsV2Error ??
		attendedLearningEvents.error ??
		null;

	return (
		<>
			<Route path="/users/:userId/journey/learning/:id">
				{({ match }) =>
					match && (
						<LearningEventModalID
							eventID={match.params.id!}
							returnURL={`/users/${user.id}/journey`}
						/>
					)
				}
			</Route>

			<Title>{t('journey.list.title', { firstName: user.firstName })}</Title>

			<JourneyList
				isViewer={user.isViewer}
				items={items ?? []}
				isLoading={isLoading}
				error={error ?? null}
			/>
		</>
	);
}
